import { useCollapse } from '@abyss/web/hooks/useCollapse';
import { storage } from '@abyss/web/tools/storage';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionStorage } from 'usehooks-ts';

import { Constants, LOB, ProviderTypes } from '../../../common/Constants';
import { PortalData } from '../../../common/Portal';
import {
  isMaestroMNRMember,
  isNonMaestroMNRMember,
} from '../../../common/Utils/memberUtils/memberUtils';
import { MockReciprocityProvider } from '../../../context/MockReciprocityContext';
import { PortalContext } from '../../../context/PortalContext';
import {
  getActualLanguageCode,
  getLanguage,
  setLanguage,
} from '../../../frontends/ProviderSearch/context/Internationalization/helpers';
import {
  LANGUAGES,
  LanguageObject,
  Locale,
} from '../../../frontends/ProviderSearch/context/Internationalization/types';
import { useLagoon } from '../../../hooks/useLagoon';
import { getSupportedLanguages } from '../../../hooks/useSupportedLanguages/useSupportedLanguages';
import { ConstantsLagoon } from '../../ConstantsLagoon';
import { getFeatureFlag } from '../../Utils/getFeatureFlag';
import { ObapiDemo } from '../ObapiDemo';
import { MemberInfo } from '../ObapiDemo/memberProfile';
import {
  CollapseIcon,
  CollapseIconContainer,
  DemoProviderSearchContainer,
  StyledSelectInput,
} from './DemoHeader.styled';

const IFPEarlyPayDefaults = {
  exchangeId: '1234567898',
  ndbLob: '907',
  qhpId: '40702AZ006001901',
  coverageEffectiveDate: '12-31-2025',
  zipCode: '85004',
  language: 'en-US',
  coverageType: 'M',
  portalSource: 'Ifpearlypay',
  stateCode: 'WA',
};

type Props = {
  form: any;
  providerType: string;
  searchType: string;
  onMemberDataChange?: (a: MemberInfo[]) => void;
  onMemberIdChange?: (a: number) => void;
  setMemberProfiles: (a: MemberInfo[]) => void;
  setProviderType: (a: string) => void;
  setSearchType: (a: string) => void;
};

export const DemoHeader = ({
  form = {},
  providerType = '',
  searchType = '',
  onMemberDataChange = () => {},
  onMemberIdChange = () => {},
  setMemberProfiles = () => {},
  setProviderType = () => {},
  setSearchType = () => {},
}: Props) => {
  const { t } = useTranslation();
  const { portalData } = useContext(PortalContext);
  const url = window.location.href;
  const [selectedLanguage, setSelectedLanguage] = useState(
    getLanguage().locale || LANGUAGES.en.locale
  );
  const [supportedLanguages, setSupportedLanguages] = useState<string[]>([]);
  const [lobFromStorage] = useSessionStorage<string>(
    Constants.STORAGE_KEYS.SESSION.LOB,
    ''
  );

  const isGuest = url.includes('/guest');
  const isGuestWithPlan = url.includes('/guest-with-plan');
  const isGuestPlanSelection = url.includes('/guest-plan-selection');
  const isLawwGuestPortal =
    portalData?.portalConfig?.isGuestUser &&
    portalData?.portalName?.toLowerCase() === 'laww';

  const getLob = () => {
    if (isLawwGuestPortal) {
      return 'guest-obh';
    }
    if (lobFromStorage) {
      return lobFromStorage;
    }
    if (isGuestWithPlan || isGuestPlanSelection) {
      return `${LOB.ENI}-guest`;
    }
    if (isGuest) {
      return LOB.IFPEarlyPay;
    }
    return LOB.ENI;
  };
  const lob = getLob();

  useEffect(() => {
    form.setValue('language', getLanguage().locale || LANGUAGES.en.locale);
    (async () => {
      const initialLobForLanguage = isLawwGuestPortal
        ? 'guest-obh'
        : form.getValues('lob') || lob;
      const supportedLanguages = await getSupportedLanguages(
        initialLobForLanguage
      );
      form.setValue('supportedLanguages', supportedLanguages);
      setSupportedLanguages(supportedLanguages);
    })();
  }, []);

  const featureFlags: [{ key: string; active: boolean }] =
    useLagoon('feature-flags')();
  const enableMnrCosmos = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MNR_COSMOS
  );
  const enableCns = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_CNS
  );
  const enableIFP = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_IFP
  );

  const sessionStoragePortalSource =
    storage.session.get(Constants.STORAGE_KEYS.SESSION.PORTAL_SOURCE) || {};

  const {
    collapseProps,
    buttonProps,
    isOpen: isOpenCollapse,
  } = useCollapse({ defaultIsOpen: false });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { 'aria-expanded': ariaExpanded, ...onClickButtonProp } = buttonProps;

  const setMNRPreEffectiveValues = () => {
    form.setValue('firstName', 'FNZIWLLECFMB');
    form.setValue('lastName', 'LNZQZZPRGM');
    form.setValue('dob', '10-15-1955');
    form.setValue('memberId', '992509665');
    form.setValue('groupNumber', '71590');
    form.setValue(
      'id',
      'Ro3J1yl0g5yJA3h_op5pmUAiDVMZc3Ts9mJlR7DLbpEBYKymFm79YxPMYlk8EcS8'
    );
    form.setValue('userName', '168adf307-7017-47f8-a130-dd0727e0e452');
    form.setValue('portalSource', 'aarp');
    form.setValue('bigNJsonString', '');
  };

  const setENIValues = (portalData) => {
    form.setValue('firstName', 'FNUUYDQFZCCW');
    form.setValue('lastName', 'LNLNUBNRSM');
    form.setValue('dob', '02-02-1979');
    form.setValue('memberId', '00990062383');
    form.setValue('groupNumber', '0168504');
    form.setValue('id', '');
    form.setValue('userName', '');
    form.setValue('portalSource', '');
    form.setValue('bigNJsonString', '');
    if (portalData?.portalName === 'umr') {
      form.setValue('delsysCode', '52');
    }
  };

  const setMNRMaestroValues = () => {
    form.setValue('firstName', 'DREENA');
    form.setValue('lastName', 'YAM');
    form.setValue('dob', '08-07-1949');
    form.setValue('memberId', '971787389');
    form.setValue('portalSource', 'maestro');
  };

  const setIFPEarlyPayValues = () => {
    Object.entries(IFPEarlyPayDefaults).forEach(([key, value]) => {
      form.setValue(key, value);
    });
  };

  const addLangParamToCurrentUrl = (lang: string) => {
    const url = new URL(window.location.href);
    // Use only 'locale' as the standardized URL param key for language
    url.searchParams.set('locale', lang);
    // Remove legacy 'lang' param if present to avoid confusion
    url.searchParams.delete('lang');
    window.history.pushState({}, '', url.toString());
    window.location.reload();
  };

  const regularLobOptions = [
    { value: LOB.ENI, label: LOB.ENI },
    { value: LOB.MNR, label: LOB.MNR },
    { value: LOB.CNS, label: LOB.CNS },
    { value: LOB.IFP, label: LOB.IFP },
    { value: LOB.IFPEarlyPay, label: LOB.IFPEarlyPay },
  ];

  const umrLobOptions = [{ value: LOB.ENI, label: LOB.ENI }];
  const guestOptions = [{ value: LOB.IFPEarlyPay, label: LOB.IFPEarlyPay }];

  const guestWithPlanOptions = [
    { value: `${LOB.ENI}-guest`, label: LOB.ENI },
    { value: `${LOB.MNR}-guest`, label: LOB.MNR },
    { value: `${LOB.CNS}-guest`, label: LOB.CNS },
    { value: `${LOB.IFP}-guest`, label: LOB.IFP },
  ];
  const getLobOptions = (portalData: PortalData) => {
    if (isGuestWithPlan || isGuestPlanSelection) {
      return guestWithPlanOptions;
    }
    if (isGuest) {
      return guestOptions;
    }
    if (portalData.portalName === 'umr') {
      return umrLobOptions;
    }
    return regularLobOptions;
  };

  const getLanguageOptions = (): { value: Locale; label: string }[] => [
    { value: LANGUAGES.en.locale, label: LANGUAGES.en.title },
    { value: LANGUAGES.es.locale, label: LANGUAGES.es.title },
    ...(supportedLanguages.includes(Locale.myMM)
      ? [
          {
            value: LANGUAGES.my.locale,
            label: LANGUAGES.my.title,
          },
        ]
      : []),
    ...(supportedLanguages.includes(Locale.htHT)
      ? [
          {
            value: LANGUAGES.ht.locale,
            label: LANGUAGES.ht.title,
          },
        ]
      : []),
    ...(supportedLanguages.includes(Locale.zhTW)
      ? [
          {
            value: LANGUAGES.zh.locale,
            label: LANGUAGES.zh.title,
          },
        ]
      : []),
    ...(supportedLanguages.includes(Locale.viVN)
      ? [
          {
            value: LANGUAGES.vi.locale,
            label: LANGUAGES.vi.title,
          },
        ]
      : []),
    ...(supportedLanguages.includes(Locale.ilPH)
      ? [
          {
            value: LANGUAGES.ic.locale,
            label: LANGUAGES.ic.title,
          },
        ]
      : []),
    ...(supportedLanguages.includes(Locale.koUS)
      ? [
          {
            value: LANGUAGES.ko.locale,
            label: LANGUAGES.ko.title,
          },
        ]
      : []),
  ];
  const lobOptions = getLobOptions(portalData);

  const handleLangaugeChange = (e) => {
    form.setValue('language', e);
    setSelectedLanguage(e);
    if (portalData?.portalConfig?.language) {
      const selectedValue = e;
      const chosenLanguage =
        LANGUAGES[getActualLanguageCode(selectedValue, supportedLanguages)];
      // for guest with plan user, we add lang param to url while submitting the form
      if (
        !portalData?.portalConfig?.basePath.startsWith('/guest-with-plan') ||
        !portalData?.portalConfig?.basePath.startsWith('guest-plan-selection')
      ) {
        addLangParamToCurrentUrl(chosenLanguage.locale);
      }
    } else {
      const selectedValue = e;
      const chosenLanguage =
        LANGUAGES[getActualLanguageCode(selectedValue, supportedLanguages)];
      setLanguage(chosenLanguage)
        .then((lang: LanguageObject) => {
          setSelectedLanguage(lang.locale);
          window.location.reload();
        })
        .catch((lang: LanguageObject) => {
          throw new Error(`${lang.title} is already the selected language`);
        });
      setSelectedLanguage(chosenLanguage.locale);
    }
  };

  const placeHolder = t('Pick one');

  useEffect(() => {
    const openDemoHeader = () => {
      onClickButtonProp.onClick();
    };
    window.addEventListener('demoHeaderListener', openDemoHeader);

    return () => {
      window.removeEventListener('demoHeaderListener', openDemoHeader);
    };
  }, [onClickButtonProp]);

  return (
    <React.Fragment>
      <CollapseIconContainer>
        <CollapseIcon
          data-testid={
            isOpenCollapse ? 'keyboard_arrow_up' : 'keyboard_arrow_down'
          }
          {...onClickButtonProp}
          color="$primary1"
          icon={isOpenCollapse ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
        />
      </CollapseIconContainer>
      {isOpenCollapse ? (
        <div {...collapseProps}>
          <DemoProviderSearchContainer>
            {LANGUAGES ? (
              <StyledSelectInput
                data-auto-testid="pick-one-language"
                data-testid="pick-one-language"
                label="Language"
                onChange={handleLangaugeChange}
                options={getLanguageOptions()}
                placeholder="Select Language"
                value={selectedLanguage}
              />
            ) : null}
            {enableMnrCosmos || enableCns || enableIFP ? (
              <StyledSelectInput
                data-auto-testid="pick-one-lob"
                data-testid="pick-one-lob"
                isSearchable
                label="LOB"
                onChange={async (e) => {
                  form?.setValue('lob', e);
                  const supportedLanguages = await getSupportedLanguages(
                    e.toString()
                  );
                  form?.setValue('supportedLanguages', supportedLanguages);
                  setSupportedLanguages(supportedLanguages);
                  if (!supportedLanguages.includes(selectedLanguage)) {
                    setSelectedLanguage(LANGUAGES.en.locale);
                    form.setValue('language', LANGUAGES.en.locale);
                  }
                  if (
                    e === LOB.MNR &&
                    isNonMaestroMNRMember(e, sessionStoragePortalSource)
                  ) {
                    setMNRPreEffectiveValues();
                  }

                  if (e === LOB.ENI) {
                    setENIValues(portalData);
                  }

                  if (e === LOB.IFPEarlyPay) {
                    setIFPEarlyPayValues();
                  }

                  if (
                    e === LOB.MNR &&
                    isMaestroMNRMember(e, sessionStoragePortalSource)
                  ) {
                    setMNRMaestroValues();
                  }
                }}
                options={lobOptions}
                placeholder="Select LOB"
                value={lob}
              />
            ) : null}
            <MockReciprocityProvider>
              <ObapiDemo
                isGuest={isGuest}
                setMemberId={onMemberIdChange}
                setMemberProfiles={(memberProfile) => {
                  setMemberProfiles(memberProfile);
                  onMemberDataChange(memberProfile);
                }}
              />
            </MockReciprocityProvider>
            <StyledSelectInput
              data-auto-testid="pick-one-search-type"
              data-testid="pick-one-search-type"
              isClearable
              isSearchable
              label="Search Type"
              onChange={(e) => {
                form?.setValue('searchType', e);
                setSearchType(e.toString());
              }}
              onClear={() => setSearchType('')}
              options={[
                { value: 'name', label: 'Name' },
                { value: 'speciality', label: 'Speciality' },
                { value: 'location', label: 'Location' },
              ]}
              placeholder={placeHolder}
              value={searchType}
            />
            <StyledSelectInput
              data-auto-testid="pick-one-provider-type"
              data-testid="pick-one-provider-type"
              isClearable
              isSearchable
              label="Provider Type"
              onChange={(e) => {
                form?.setValue('providerType', e);
                setProviderType(e.toString());
              }}
              onClear={() => setProviderType('')}
              options={[
                {
                  value: ProviderTypes.PRACTITIONER,
                  label: 'Practitioner',
                },
                {
                  value: ProviderTypes.ORGANIZATION,
                  label: 'Facility',
                },
              ]}
              placeholder={placeHolder}
              value={providerType}
            />
          </DemoProviderSearchContainer>
        </div>
      ) : null}
    </React.Fragment>
  );
};
